import React from 'react';
import { InvoiceData, InvoiceSettings } from '../../../types';

interface Props {
  data: InvoiceData;
  settings: InvoiceSettings;
  labels: Record<string, string>;
  subtotal: number;
  taxTotal: number;
  total: number;
}

const SpreadsheetTemplate: React.FC<Props> = ({ data, settings, subtotal, total }) => {
  const currencySymbol = settings.currencySymbol || '$';

  return (
    <div className="p-8 max-w-4xl mx-auto bg-white text-slate-900 font-mono text-[11px] min-h-screen">
      <div className="flex justify-between items-center bg-slate-100 border border-slate-200 p-2 mb-8 rounded-lg">
        <div className="flex items-center gap-2">
          {data.logo ? (
            <img
              src={data.logo}
              alt="Logo"
              className="w-8 h-8 object-contain rounded bg-white p-0.5"
            />
          ) : (
            <div className="w-6 h-6 bg-emerald-600 rounded"></div>
          )}
          <span className="font-bold text-emerald-800">Invoice_Export_{data.number}.xlsx</span>
        </div>
        <div className="flex gap-2 text-[9px] uppercase font-bold text-slate-400">
          <span>File</span>
          <span>Edit</span>
          <span>View</span>
          <span>Data</span>
        </div>
      </div>

      <div className="grid grid-cols-12 border border-slate-200">
        <div className="col-span-12 bg-emerald-50 border-b border-slate-200 p-4 flex justify-between">
          <h1 className="text-lg font-black uppercase text-emerald-900 tracking-tighter">
            Professional Billing Statement
          </h1>
          <div className="text-right">
            <p className="font-bold">STATUS: {data.status}</p>
            <p className="text-slate-400">ID: {data.id}</p>
          </div>
        </div>

        <div className="col-span-6 border-r border-slate-200 p-6">
          <h3 className="bg-slate-900 text-white px-2 py-0.5 inline-block font-bold uppercase mb-4">
            Issuer Information
          </h3>
          <p className="font-black text-sm">{data.senderName}</p>
          <p className="whitespace-pre-wrap leading-relaxed mt-2 text-slate-500">
            {data.senderAddress}
          </p>
        </div>
        <div className="col-span-6 p-6 bg-slate-50">
          <h3 className="bg-slate-900 text-white px-2 py-0.5 inline-block font-bold uppercase mb-4">
            Billing Recipient
          </h3>
          <p className="font-black text-sm">{data.recipientCompany || data.recipientName}</p>
          <p className="whitespace-pre-wrap leading-relaxed mt-2 text-slate-500">
            {data.recipientAddress}
          </p>
        </div>

        <div className="col-span-12">
          <table className="w-full border-collapse">
            <thead className="bg-slate-100 text-slate-500">
              <tr>
                <th className="border border-slate-200 p-3 text-left w-12 text-slate-300">#</th>
                <th className="border border-slate-200 p-3 text-left">DESCRIPTION</th>
                <th className="border border-slate-200 p-3 text-center w-20">QTY</th>
                <th className="border border-slate-200 p-3 text-right w-32">UNIT_RATE</th>
                <th className="border border-slate-200 p-3 text-right w-32">LINE_TOTAL</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((item, idx) => (
                <tr key={idx}>
                  <td className="border border-slate-100 p-3 text-center text-slate-300">
                    {idx + 1}
                  </td>
                  <td className="border border-slate-100 p-3 font-bold">{item.description}</td>
                  <td className="border border-slate-100 p-3 text-center">{item.quantity}</td>
                  <td className="border border-slate-100 p-3 text-right">{item.rate.toFixed(2)}</td>
                  <td className="border border-slate-100 p-3 text-right font-black bg-slate-50/50">
                    {(item.quantity * item.rate).toFixed(2)}
                  </td>
                </tr>
              ))}
              {[...Array(5)].map((_, i) => (
                <tr key={`empty-${i}`}>
                  <td className="border border-slate-50 p-3 h-8"></td>
                  <td className="border border-slate-50 p-3 h-8"></td>
                  <td className="border border-slate-50 p-3 h-8"></td>
                  <td className="border border-slate-50 p-3 h-8"></td>
                  <td className="border border-slate-50 p-3 h-8"></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="col-span-8 p-6 bg-slate-950 text-emerald-500">
          <p className="font-bold mb-2">:: SYSTEM_NOTES</p>
          <p className="whitespace-pre-wrap">{data.paymentInfo || 'No extra data provided.'}</p>
        </div>
        <div className="col-span-4 p-6 bg-slate-100">
          <div className="flex justify-between mb-2">
            <span className="font-bold text-slate-400">SUBTOTAL</span>
            <span className="font-bold">
              {currencySymbol}
              {subtotal.toFixed(2)}
            </span>
          </div>
          <div className="flex justify-between pt-4 mt-4 border-t-2 border-slate-900">
            <span className="font-black text-lg">TOTAL</span>
            <span className="font-black text-lg text-brand-600">
              {currencySymbol}
              {total.toFixed(2)}
            </span>
          </div>
        </div>
      </div>

      <div className="mt-8 flex justify-center gap-10 text-[9px] text-slate-300 font-bold uppercase tracking-widest">
        <span>Encoded: SHA-256</span>
        <span>Hash: 8XJ1-992K-11LL</span>
        <span>Generated: {new Date().toLocaleTimeString()}</span>
      </div>
    </div>
  );
};

export default SpreadsheetTemplate;
